/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description CancelBatchEligibilityRequest button
 */

import { useState } from 'react';
import _ from 'lodash';
import { Button, ButtonProps, Input, Modal, Tooltip } from 'antd';

import EventTracker from 'utils/eventTracker';
import useCancelBatchEligibilityRequest from 'hooks/eligibility/mutate/useCancelBatchEligibilityRequest';
import { ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES, ELIGIBILITY_BATCH_ACTIONS_MODAL_CONFIG } from 'app-constants/eligibility';
import { EVENTS, EVENT_ENTITIES } from 'app-constants/events';

const DRAFT_SUBMITTED_STATUSES = [ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.inProgress, ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.scheduled];

export interface IProps extends ButtonProps {
	onBatchCancel?: VoidFunction;
	entity?: string;
	data: EligibilityBatchUpload.IBatchEligibilityResponseList;
}

/**
 * CancelBatchEligibilityRequest  button
 */
const CancelBatchEligibilityRequest: React.FC<IProps> = ({ title, entity, ...props }) => {
	const { data, disabled, onBatchCancel, ...restProps } = props;
	const [reason, setReason] = useState('');
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { mutate, isPending } = useCancelBatchEligibilityRequest();

	const { tooltipTitle, okText, cancelText, modalDescriptionText, Icon } = _.includes(DRAFT_SUBMITTED_STATUSES, data?.status)
		? ELIGIBILITY_BATCH_ACTIONS_MODAL_CONFIG.active
		: ELIGIBILITY_BATCH_ACTIONS_MODAL_CONFIG.draft;

	// function to cancel batch request
	const cancelBatchRequest = () => {
		mutate(
			{ batchId: data.batchId, reason },
			{
				onSettled: () => setIsModalVisible(false),
				onSuccess: () => {
					onBatchCancel?.();
				},
			},
		);
		EventTracker.trackEvent(EVENTS.eligibilityBatchUpload.deleteBatch(data?.sequenceId, entity || EVENT_ENTITIES.batchEdit));
	};

	return (
		<>
			<Tooltip title={title ?? tooltipTitle}>
				<Button
					data-testid='batch-cancel-btn'
					key='cancel-batch-request'
					danger
					icon={<Icon />}
					disabled={disabled || isPending}
					onClick={() => setIsModalVisible(true)}
					{...restProps}
				/>
			</Tooltip>
			<Modal
				data-testid='batch-cancel-modal'
				title={`Batch Number: ${data?.sequenceId}`}
				closable={true}
				maskClosable={false}
				open={isModalVisible}
				okText={okText}
				cancelText={cancelText}
				onOk={cancelBatchRequest}
				onCancel={() => setIsModalVisible(false)}
				cancelButtonProps={{ disabled: isPending }}
				okButtonProps={{ loading: isPending, disabled: _.size(reason) < 3, 'data-testid': 'batch-cancel-confirm-btn' }}
			>
				<>
					<div className='mx-0 my-3'>{modalDescriptionText}</div>
					<Input
					data-testid='batch-cancel-reason-input'
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder='Comment'
					style={{ marginBottom: 12 }}
				/>
				</>
			</Modal>
		</>
	);
};

export default CancelBatchEligibilityRequest;
